/* AdminTradeScoreDisputes — admin Trade Score forensic console (Iter 167).
 *
 * Mounted at /admin/trade-score-disputes. Admin-only.
 *
 * Two-pane layout:
 *   LEFT  — list of disputes (filter by status: open/resolved/all)
 *   RIGHT — selected dispute drill-down with:
 *           • Buyer identity (email, company, EIN, created_at)
 *           • Filing forensics (client IP, user agent, evidence URL)
 *           • Current score envelope (admin sees opt-out-bypassed view)
 *           • Underlying invoices (proves what data fed the score)
 *           • Full audit event log (every view + state change)
 *           • Resolve dispute form
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, Building2, Globe, Activity, FileText, Receipt, ScrollText,
  CheckCircle2, X, AlertCircle, ChevronRight,
} from "lucide-react";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const fmtUsd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function AdminTradeScoreDisputes() {
  const [status, setStatus]       = useState("open");
  const [disputes, setDisputes]   = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail]       = useState(null);

  const loadList = async (st = status) => {
    try { const r = await api.get(`/admin/trade-score/disputes?status=${st}&limit=200`);
      setDisputes(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load disputes"); }
  };
  useEffect(() => { loadList(status); /* eslint-disable-next-line */ }, [status]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/admin/trade-score/disputes/${selectedId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load detail"));
  }, [selectedId]);

  return (
    <div data-testid="admin-trade-score-disputes">
      <PageHeader
        title="Trade Score · Disputes"
        subtitle="Forensic record for every Verqo Trade Score dispute. Defensive audit trail."
        action={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="dispute-status-filter" className="w-44 rounded-full">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">

        {/* List */}
        <section className="rounded-2xl border border-border bg-white"
                 data-testid="dispute-list">
          <div className="px-4 py-3 border-b border-border">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              {disputes ? `${disputes.count} ${status} dispute${disputes.count !== 1 ? "s" : ""}` : "Loading…"}
            </div>
          </div>
          {!disputes ? null : disputes.count === 0 ? (
            <div className="p-10 text-center text-[13px] text-foreground/55" data-testid="dispute-empty">
              <ShieldAlert className="w-6 h-6 mx-auto mb-2 text-foreground/30"/>
              No {status} disputes to review.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[680px] overflow-y-auto">
              {disputes.rows.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setSelectedId(d.id)}
                  data-testid={`dispute-row-${d.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-secondary/30 ${selectedId === d.id ? "bg-secondary/40 border-l-2 border-l-foreground" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] mono uppercase tracking-wider ${
                      d.status === "open"
                        ? "bg-amber-50 text-amber-800 border border-amber-200"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}>
                      {d.status}
                    </span>
                    <span className="text-[12.5px] font-medium truncate flex-1">
                      {d.buyer_company || d.buyer_email || "—"}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-foreground/40"/>
                  </div>
                  <div className="text-[11.5px] text-foreground/65 truncate">{d.reason}</div>
                  <div className="text-[10px] mono text-foreground/45 mt-1">
                    {d.client_ip ? <span><Globe className="w-2.5 h-2.5 inline mr-0.5"/>{d.client_ip} · </span> : null}
                    {(d.created_at || "").slice(0, 19).replace("T", " ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Detail */}
        <section data-testid="dispute-detail">
          {!selectedId ? (
            <div className="rounded-2xl border border-dashed border-border bg-secondary/10 p-10 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-foreground/30"/>
              <div className="text-[13.5px] text-foreground/60">Select a dispute on the left to see the full forensic record.</div>
            </div>
          ) : !detail ? (
            <div className="rounded-2xl border border-border bg-white p-6 text-[13px] text-foreground/55">Loading detail…</div>
          ) : (
            <DisputeDetailPanel detail={detail} onResolved={() => { loadList(); setSelectedId(null); setDetail(null); }}/>
          )}
        </section>
      </div>
    </div>
  );
}

function DisputeDetailPanel({ detail, onResolved }) {
  const d   = detail.dispute;
  const b   = detail.buyer;
  const env = detail.current_envelope || {};
  const ip  = (d.metadata || {}).client_ip;
  const ua  = (d.metadata || {}).user_agent;
  const ev  = (d.metadata || {}).evidence_url;

  return (
    <div className="space-y-5">
      {/* Buyer identity */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="detail-identity">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-4 h-4 text-foreground/55"/>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Buyer identity</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
          <Kv label="Company"  v={b.company || b.name}/>
          <Kv label="Email"    v={b.email}/>
          <Kv label="EIN"      v={b.ein}/>
          <Kv label="Joined"   v={(b.created_at || "").slice(0, 10)}/>
        </div>
      </div>

      {/* Dispute filing forensics */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="detail-forensics">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-foreground/55"/>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Dispute · filing forensics</div>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-wider ${
            d.status === "open"
              ? "bg-amber-50 text-amber-800 border border-amber-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}>
            {d.status}
          </span>
        </div>
        <div className="rounded-lg bg-foreground/[0.03] border border-border p-3 text-[13px] mb-3" data-testid="dispute-reason">
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/55 mb-1">Buyer-supplied reason</div>
          {d.reason}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px]">
          <Kv label="Filed at"     v={(d.created_at || "").slice(0, 19).replace("T", " ")}/>
          <Kv label="Client IP"    v={ip || "—"}      mono/>
          <Kv label="Referer"      v={(d.metadata || {}).referer || "—"} mono/>
          <Kv label="Evidence URL" v={ev || "—"} mono/>
          <Kv label="User agent"   v={ua || "—"} mono className="md:col-span-2 truncate"/>
        </div>
        {d.status === "resolved" && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-[12.5px]">
            <div className="text-[10px] mono uppercase tracking-wider text-emerald-800 mb-1">Resolution</div>
            <strong className="capitalize">{d.resolution}</strong> · {d.notes}
            <div className="text-[10.5px] text-foreground/55 mono mt-1">{(d.resolved_at || "").slice(0, 19).replace("T", " ")}</div>
          </div>
        )}
      </div>

      {/* Current score envelope */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="detail-envelope">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-foreground/55"/>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Current Trade Score envelope</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px]">
          <Kv label="Score"        v={env.score}/>
          <Kv label="Band"         v={env.band}/>
          <Kv label="Maturity"     v={env.maturity}/>
          <Kv label="Confidence"   v={env.confidence}/>
        </div>
        <div className="mt-3 text-[11.5px] text-foreground/60">
          <strong>Source mix:</strong>{" "}
          {env.data_source_mix?.external_pct}% external · {env.data_source_mix?.behavioral_pct}% behavioral · {env.data_source_mix?.sources_available} live source(s)
        </div>
        <details className="mt-3">
          <summary className="text-[11.5px] mono uppercase tracking-wider text-foreground/55 cursor-pointer">Raw envelope JSON</summary>
          <pre className="text-[10.5px] mono bg-foreground/[0.04] border border-border rounded-md p-2 mt-2 overflow-x-auto max-h-72">
{JSON.stringify(env, null, 2)}
          </pre>
        </details>
      </div>

      {/* Underlying invoices */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="detail-invoices">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-4 h-4 text-foreground/55"/>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Underlying invoices · {detail.invoice_count}</div>
        </div>
        {detail.invoice_count === 0 ? (
          <div className="text-[12.5px] text-foreground/55 italic">No invoices on file — score is currently 100% external-signals driven.</div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] mono uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="text-left py-1.5">Code</th>
                  <th className="text-left">Seller</th>
                  <th className="text-right">Amount</th>
                  <th className="text-left pl-3">Status</th>
                  <th className="text-right">Due</th>
                  <th className="text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {detail.invoices.slice(0, 30).map((inv) => (
                  <tr key={inv.id} className="border-t border-border/60">
                    <td className="py-1.5 mono">{inv.code || inv.id?.slice(0, 8)}</td>
                    <td className="truncate max-w-[160px]">{inv.seller_name || "—"}</td>
                    <td className="text-right tabular-nums">{fmtUsd(inv.amount || inv.total)}</td>
                    <td className="pl-3 mono text-[10.5px] uppercase">{inv.status}</td>
                    <td className="text-right text-[10.5px] text-foreground/55">{(inv.due_date || "").slice(0, 10)}</td>
                    <td className="text-right text-[10.5px] text-foreground/55">{(inv.paid_at || "").slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit log */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="detail-audit">
        <div className="flex items-center gap-2 mb-3">
          <ScrollText className="w-4 h-4 text-foreground/55"/>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Audit log · {detail.audit_event_count} event(s)</div>
        </div>
        {detail.audit_event_count === 0 ? (
          <div className="text-[12.5px] text-foreground/55 italic">No audit events yet.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto divide-y divide-border/60">
            {detail.audit_events.slice(0, 100).map((a, i) => (
              <div key={i} className="py-1.5 text-[12px] flex items-center gap-3">
                <span className="mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-foreground/[0.04] border border-border/60 w-32 shrink-0">
                  {a.event}
                </span>
                <span className="flex-1 truncate text-foreground/70">
                  {a.actor_role || "—"} · {a.actor_id?.slice(0, 12) || "—"}
                  {a.metadata?.band ? ` · band ${a.metadata.band}` : ""}
                  {a.reason ? ` · ${a.reason.slice(0, 80)}` : ""}
                </span>
                <span className="text-[10.5px] mono text-foreground/45 shrink-0">{(a.ts || "").slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resolve action */}
      {d.status === "open" && (
        <ResolveForm disputeId={d.id} onResolved={onResolved}/>
      )}
    </div>
  );
}

function ResolveForm({ disputeId, onResolved }) {
  const [resolution, setResolution] = useState("rejected");
  const [notes, setNotes]           = useState("");
  const [busy, setBusy]             = useState(false);

  const submit = async () => {
    if (notes.trim().length < 3) { toast.error("Resolution notes required (≥3 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/trade-score/disputes/${disputeId}/resolve`, { resolution, notes });
      toast.success(`Dispute ${resolution}`);
      onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="resolve-form">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-4 h-4 text-foreground/55"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Resolve dispute</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <Label className="text-[11px] text-foreground/55">Resolution</Label>
          <Select value={resolution} onValueChange={setResolution}>
            <SelectTrigger data-testid="resolve-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="upheld">Upheld · buyer was right</SelectItem>
              <SelectItem value="rejected">Rejected · score stands</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] text-foreground/55">Ops notes (will be emailed to buyer)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    data-testid="resolve-notes"
                    placeholder="e.g. Confirmed INV-1234 was paid via wire on 5/12. Corrected ledger; score recomputed."/>
        </div>
      </div>
      <Button onClick={submit} disabled={busy || notes.trim().length < 3}
              data-testid="resolve-submit"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
        {busy ? "Resolving…" : "Close dispute"}
      </Button>
    </div>
  );
}

function Kv({ label, v, mono, className = "" }) {
  return (
    <div className={`rounded-md bg-secondary/30 border border-border px-2.5 py-1.5 ${className}`}>
      <div className="text-[9.5px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className={`text-[12.5px] font-medium mt-0.5 truncate ${mono ? "mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}
